import {
  LearningObjectiveStatementStatus,
  OrderType,
  useSearchLearningObjectiveStatementsLazyQuery,
} from '@exam-management/graphql';
import { APP_PERMISSIONS } from '@exam-management/permissions';
import {
  FormSearchSelection,
  type FormSearchSelectionProps,
  type OptionSelectionProps,
} from '@shared/components';
import { useDisclosure } from '@shared/hooks';
import { useHasPermission } from '@shared/permissions';
import { useCallback, useEffect, useState } from 'react';
import { useFormContext, type FieldValues } from 'react-hook-form';

export interface LearningObjectiveStatementFieldProps<TFieldValues extends FieldValues>
  extends Omit<FormSearchSelectionProps<TFieldValues>, 'options' | 'label'> {
  chapterLevelItemId: string | null;
  helperText?: React.ReactNode;
}

export default function LearningObjectiveStatementField<TFieldValues extends FieldValues>(
  props: Readonly<LearningObjectiveStatementFieldProps<TFieldValues>>,
) {
  const { name, helperText, chapterLevelItemId, ...restProps } = props;
  const [isOpen, { open, close }] = useDisclosure();
  const [losOptions, setLosOptions] = useState<OptionSelectionProps[]>([]);

  const { setValue } = useFormContext();

  const isHasViewPermission = useHasPermission(APP_PERMISSIONS.QBANK.LOS.VIEW);

  const [searchLOSResult, { loading: isSearchLOSLoading }] =
    useSearchLearningObjectiveStatementsLazyQuery({
      onCompleted: (data) => {
        const losOptions =
          data.qbsSearchLearningObjectiveStatements.learningObjectiveStatements.map((los) => {
            return { label: los.tag.name, value: los.id };
          }) ?? [];
        setLosOptions(losOptions);
      },
      fetchPolicy: 'network-only',
    });

  const handleOpenOptions = useCallback(async () => {
    if (chapterLevelItemId) {
      await searchLOSResult({
        variables: {
          search: {
            chapterLevelItemId,
            status: LearningObjectiveStatementStatus.Published,
          },
          paging: {
            pageSize: 100,
            currentPage: 0,
          },
          order: {
            field: 'updatedAt',
            type: OrderType.Asc,
          },
        },
      });
    }
    open();
  }, [chapterLevelItemId, open, searchLOSResult]);

  const noOptionsText = !isHasViewPermission
    ? 'ไม่มีสิทธิ์การเข้าถึง กรุณาติดต่อผู้ดูแลระบบ'
    : (helperText ?? 'ไม่พบข้อมูล');
  const shouldDisabled = !chapterLevelItemId;

  useEffect(() => {
    if (!chapterLevelItemId) {
      setLosOptions([]);
      setValue(name as Parameters<typeof setValue>[0], null);
    }
  }, [chapterLevelItemId]);

  return (
    <FormSearchSelection
      label='วัตถุประสงค์การเรียนรู้ (Learning Objective Statement (LOS))'
      options={isHasViewPermission ? losOptions : []}
      name={name}
      placeholder='กรุณาเลือกวัตถุประสงค์การเรียนรู้ (Learning Objective Statement (LOS))'
      loading={isSearchLOSLoading}
      noOptionsText={noOptionsText}
      open={isOpen}
      onOpen={handleOpenOptions}
      disabled={shouldDisabled}
      onClose={close}
      {...restProps}
    />
  );
}
